import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformName } from '../config/platforms';

const API_URL = process.env.REACT_APP_API_URL || '';

export interface ActionablePost {
  id: string;
  status: string;
  platforms: string[];
  scheduled_for?: string | null;
  /** Per-platform publish times. Absent platforms use scheduled_for. */
  schedule_variants?: Record<string, string> | null;
  results?: { platform: string; status: string; error_message?: string }[];
}

/**
 * Publish, schedule and delete a post, with the outcome reported honestly.
 *
 * Shared by Post History and the Calendar so both act on a post identically. The important
 * part is `publish()`: the endpoint returns 207 when a post only partly went out, which is
 * still a 2xx and therefore still `response.ok`. Treating that as plain success is exactly
 * the bug the rejected/failed split exists to fix, so the per-platform results are
 * inspected and summarised rather than trusting the status code.
 */
export const usePostActions = (onChanged: () => void) => {
  const { token } = useAuth();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const clearMessages = useCallback(() => {
    setError('');
    setNotice('');
  }, []);

  const request = useCallback(
    async (postId: string, path: string, init: RequestInit = {}) => {
      setBusyId(postId);
      setError('');
      setNotice('');
      try {
        const response = await fetch(`${API_URL}${path}`, {
          ...init,
          headers: {
            Authorization: `Bearer ${token}`,
            ...(init.body ? { 'Content-Type': 'application/json' } : {}),
            ...(init.headers || {}),
          },
        });
        // Some endpoints answer with no body (delete); guard the parse
        const text = await response.text();
        const data = text ? JSON.parse(text) : {};
        return { ok: response.ok, status: response.status, data };
      } catch {
        return { ok: false, status: 0, data: { error: 'Network error' } };
      } finally {
        setBusyId(null);
      }
    },
    [token]
  );

  const publish = useCallback(
    async (post: ActionablePost) => {
      const { ok, data } = await request(post.id, `/api/posts/${post.id}/publish`, { method: 'POST' });

      if (!ok) {
        setError(data.message || data.error || 'Could not publish the post');
        return;
      }

      // A 207 is ok:true. Summarise per platform so a partial publish never reads as a
      // clean success.
      const results: { platform: string; status: string; error_message?: string }[] = data.results || [];
      const published = results.filter(r => r.status === 'success');
      const notSent = results.filter(r => r.status !== 'success');

      if (!published.length) {
        setError(
          `Nothing was published. ${notSent
            .map(r => `${r.platform}: ${(r.error_message || '').replace(/^[a-z_]+:\s*/, '')}`)
            .join('; ')}`
        );
      } else if (notSent.length) {
        setNotice(
          `Published to ${published.map(r => r.platform).join(', ')}. ` +
          `Not sent to ${notSent
            .map(r => `${r.platform} (${(r.error_message || '').replace(/^[a-z_]+:\s*/, '')})`)
            .join('; ')}.`
        );
      } else {
        setNotice(`Published to ${published.map(r => r.platform).join(', ')}.`);
      }

      onChanged();
    },
    [request, onChanged]
  );

  const schedule = useCallback(
    async (
      post: ActionablePost,
      isoDate: string | null,
      variants: Record<string, string> = {}
    ) => {
      const { ok, data } = await request(post.id, `/api/posts/${post.id}/schedule`, {
        method: 'POST',
        body: JSON.stringify({ scheduledFor: isoDate, scheduleVariants: variants }),
      });

      if (!ok) {
        setError(data.message || data.error || 'Could not schedule the post');
        return false;
      }

      const short = (iso: string) =>
        new Date(iso).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });

      // A staggered post gets each network named with its own time. "Scheduled for Tue 9am"
      // would be wrong for the networks that go out later, and this notice is the only
      // confirmation the person sees.
      const times = post.platforms.map(p => ({ platform: p, at: variants[p] || isoDate }));
      const staggered = isoDate && new Set(times.map(t => t.at)).size > 1;

      setNotice(
        !isoDate
          ? 'Moved to Idea Staging.'
          : staggered
            ? `Scheduled — ${times
                .sort((a, b) => String(a.at).localeCompare(String(b.at)))
                .map(t => `${t.platform} ${short(t.at as string)}`)
                .join(', ')}.`
            : `Scheduled for ${short(isoDate)}.`
      );
      onChanged();
      return true;
    },
    [request, onChanged]
  );

  /**
   * Take a post off the schedule and back into Idea Staging.
   *
   * Staging is the drafts view, so this is the same clear-the-schedule request as
   * `schedule(post, null)`. Everything stays, times included: they do nothing while it is a
   * draft, and scheduling it again starts from them. Its own action so the notice names where the post went, rather than the
   * draft-centric wording the reschedule dialog uses.
   */
  const stage = useCallback(
    async (post: ActionablePost) => {
      const { ok, data } = await request(post.id, `/api/posts/${post.id}/schedule`, {
        method: 'POST',
        body: JSON.stringify({ scheduledFor: null }),
      });
      if (!ok) {
        setError(data.message || data.error || 'Could not move the post to Idea Staging');
        return false;
      }
      setNotice('Moved to Idea Staging. Its times are kept for when you schedule it again.');
      onChanged();
      return true;
    },
    [request, onChanged]
  );

  /**
   * Send a post to one network again, leaving the others alone.
   *
   * `republish` is for a network where the post went out and was then deleted on the platform
   * by hand; the server refuses it otherwise, and checks Facebook and Instagram itself.
   */
  const retry = useCallback(
    async (post: ActionablePost, platform: string, republish = false, mediaUrls?: string[]) => {
      const { ok, status, data } = await request(post.id, `/api/posts/${post.id}/retry`, {
        method: 'POST',
        body: JSON.stringify({ platform, republish, ...(mediaUrls ? { mediaUrls } : {}) }),
      });
      if (!ok) {
        setError(data.message || data.error || 'Could not retry the post');
        return false;
      }

      // 207 is ok:true but means the network refused it again
      const result = (data.results || []).find((r: { platform: string }) => r.platform === platform);
      const name = getPlatformName(platform);
      if (status === 207 || result?.status !== 'success') {
        setError(
          `${name} did not take it this time either: ${(result?.error_message || 'no reason given').replace(/^[a-z_]+:\s*/, '')}`
        );
      } else {
        setNotice(`${republish ? 'Republished' : 'Published'} to ${name}${mediaUrls ? ' with the new image' : ''}.`);
      }
      onChanged();
      return true;
    },
    [request, onChanged]
  );

  /**
   * Delete a post, optionally taking the live copies down from the platforms too.
   *
   * Like publish(), this cannot trust the status code: a partial removal answers 207, which
   * is still `ok`. When any platform refused, the server deliberately keeps the post so the
   * failure stays visible and retryable — so this reports which platform still holds it
   * rather than saying "deleted" over a post that is still live.
   */
  const remove = useCallback(
    async (post: ActionablePost, removeFromPlatforms = false) => {
      const { ok, data } = await request(post.id, `/api/posts/${post.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ removeFromPlatforms }),
      });
      if (!ok) {
        setError(data.message || data.error || 'Could not delete the post');
        return false;
      }

      const removals: { platform: string; success: boolean; error?: string }[] = data.removals || [];
      const failed = removals.filter(r => !r.success);

      if (!data.deleted) {
        // 207: still live somewhere, so the post was kept on purpose
        setError(
          `The post was not deleted — it is still live on ${failed
            .map(f => `${f.platform} (${f.error || 'removal failed'})`)
            .join('; ')}.`
        );
        onChanged();
        return false;
      }

      const removed = removals.filter(r => r.success).map(r => r.platform);
      setNotice(
        removed.length
          ? `Post deleted, and removed from ${removed.join(', ')}.`
          : 'Post deleted.'
      );
      onChanged();
      return true;
    },
    [request, onChanged]
  );

  return { publish, schedule, stage, retry, remove, busyId, error, notice, clearMessages, setError };
};
